import { type Context, type Meter, type ObservableResult, ROOT_CONTEXT } from '@opentelemetry/api'
import stringify from 'fast-safe-stringify'
import throttle from 'lodash/throttle'

import { BACKGROUND_FLOW_CONTEXT_KEY, runInChildSpan } from '~/utils/observability'

import { type PlanningNoteCaptureBatchOutcome, type PlanningNoteObservation, sendPlanningNoteCaptureBatch } from './sendPlanningNoteCaptureBatch'

const CAPTURE_INTERVAL_MS = 60_000
const DEFAULT_BATCH_SIZE = 500
// Match MAX_OBSERVATIONS_PER_BATCH in api/routes/raw_dips_planning_note_observations.py.
const MAX_BATCH_SIZE = 5000
export type { PlanningNoteObservation } from './sendPlanningNoteCaptureBatch'

function* batched<T>(values: readonly T[], batchSize: number): Generator<T[]> {
    for (let index = 0; index < values.length; index += batchSize) {
        yield values.slice(index, index + batchSize)
    }
}

export type PlanningNoteCaptureOptions = {
    enabled: boolean
    batchSize: number
}

export type PlanningNoteCapture = {
    observe(observations: readonly PlanningNoteObservation[]): void
    stop(): void
}

/**
 * Best-effort capture of observations, including incomplete ones, deduplicated while pending.
 * One instance is created per app lifecycle; the queue metric describes that instance.
 * Batch size is fixed per send run; enablement is checked before each request attempt.
 * Producers are not blocked, and the number of distinct pending observations is not bounded.
 */
export function createPlanningNoteCapture({
    meter,
    send,
    getOptions,
    reportFailure,
}: {
    meter: Meter
    send: (observations: PlanningNoteObservation[], signal: AbortSignal) => Promise<{ response: Response }>
    getOptions: () => PlanningNoteCaptureOptions
    reportFailure: (count: number, kind: 'http' | 'unhandled') => void
}): PlanningNoteCapture {
    // Only identical pending observations are duplicates. Different payloads for the same
    // DIPS identity must reach the backend, which records them as contradictory evidence.
    // Follow-up: Avoid repeated polling traffic by tracking backend-acknowledged observations
    // in memory, preferably by reusing data already retained by the store rather than copying
    // payloads. Never persist this state or acknowledge failed/merely attempted batches. Decide
    // separately whether repeated quarantined observations remain useful evidence.
    const pending = new Map<string, PlanningNoteObservation>()
    const controller = new AbortController()
    let inFlight = false
    let activeObservationCount = 0

    const queueSize = meter.createObservableGauge('app.planning_note.capture.queue_size', {
        description: 'Observations pending in the capture queue or remaining in its active send run',
        unit: '{observation}',
    })
    const observeQueueSize = (result: ObservableResult) => {
        result.observe(pending.size, { state: 'pending' })
        result.observe(controller.signal.aborted ? 0 : activeObservationCount, { state: 'active' })

        // The SDK retains the last sample, so publish zero once before detaching a stopped capture.
        if (controller.signal.aborted) queueSize.removeCallback(observeQueueSize)
    }
    queueSize.addCallback(observeQueueSize)

    async function sendObservations(observations: PlanningNoteObservation[], context: Context): Promise<void> {
        let failedCount = 0
        let unhandled = false

        const configuredSize = getOptions().batchSize
        const batchSize = Number.isInteger(configuredSize) && configuredSize > 0 ? Math.min(configuredSize, MAX_BATCH_SIZE) : DEFAULT_BATCH_SIZE

        for (const batch of batched(observations, batchSize)) {
            if (!getOptions().enabled) return

            let outcome: PlanningNoteCaptureBatchOutcome
            try {
                outcome = await sendPlanningNoteCaptureBatch(batch, send, { signal: controller.signal, isEnabled: () => getOptions().enabled }, context)
            } finally {
                activeObservationCount -= batch.length
            }
            if (outcome === 'bailed') return
            if (outcome !== 'ok') {
                failedCount += batch.length
                if (outcome === 'failed-unhandled') unhandled = true
            }
        }

        if (failedCount > 0) reportFailure(failedCount, unhandled ? 'unhandled' : 'http')
    }

    const sendPending = throttle(
        async (): Promise<void> => {
            if (!getOptions().enabled) {
                pending.clear()
                return
            }

            if (inFlight || pending.size === 0) return

            const observations = Array.from(pending.values())
            pending.clear()

            inFlight = true
            activeObservationCount = observations.length

            try {
                await runInChildSpan(
                    'raw-dips-planning-note.capture',
                    context => sendObservations(observations, context),
                    ROOT_CONTEXT.setValue(BACKGROUND_FLOW_CONTEXT_KEY, true)
                )
            } finally {
                inFlight = false
                activeObservationCount = 0

                if (!controller.signal.aborted && pending.size > 0) void sendPending()
            }
        },
        CAPTURE_INTERVAL_MS,
        { leading: true, trailing: true }
    )

    return {
        observe(observations) {
            if (controller.signal.aborted) return

            if (!getOptions().enabled) {
                pending.clear()
                sendPending.cancel()
                return
            }

            for (const observation of observations) {
                pending.set(stringify.stableStringify(observation), observation)
            }

            if (observations.length > 0) void sendPending()
        },

        stop() {
            pending.clear()
            sendPending.cancel()
            controller.abort()
        },
    }
}
